import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLeagueApi } from "../../data/LeagueEnvironmentProvider";
import type { GameShotProfileSide, ReportSourceInfo } from "../types";
import { CONDENSED_FONT_FAMILY, GAME_SHOT_SAMPLE_FLOOR, REPORT_HOVER_CARD_SX } from "../constants";
import PercentileChip from "../PercentileChip";
import GreyNumeral from "../GreyNumeral";
import ShotMarksKey from "../ShotMarksKey";
import { colorRank, getPercentileFromRankOrPercentile, nth } from "../utils";
import { SHOT_MARKS, tsHexFill } from "../../util/reportColors";
import type {
  ShotProfileCell,
  ShotProfileData,
} from "../../models/PlayerStats";
import { COURT_CONSTANTS } from "../ShotDetailModal/types";
import type { Shot, ShotQueryContext } from "../ShotDetailModal/types";
import { hexTipText, isFga, isTsa, mergeAdjacentBins } from "../ShotDetailModal/utils";
import { CELL_SX, HEAD_SX, ROW_LABEL_SX, SectionHead, ZEBRA_SX } from "./sections";
import { shotQualLabel } from "../ShotQualLabel";

/**
 * Personnel-report shot block: hexbin half court + shot distribution by zone +
 * 3PT shot detail (open C&S / contested C&S / off-dribble), computed
 * client-side from the shot_detail endpoint (the hexbin is per-player-only
 * content — deliberately NOT blob data). Ports the personnel report's court
 * SVG, TS color ramp, and d3-hexbin lattice assignment so the page matches
 * the printed report.
 */

// feet: the drill-down's hex size too (COURT_CONSTANTS.HEXAGON_RADIUS), so both charts bin on one lattice
const HEX_R = COURT_CONSTANTS.HEXAGON_RADIUS;
// Larger than personnel print (6.2) but shy of dominating the row — sized down
// from 10 after visual review (user call 2026-08-25).
const COURT_K = 8; // px per foot
const COURT_CUT = 38; // feet of court shown

// Zone folding over Shot.region (models/ss.py simple_shot_region vocabulary).
const COARSE_ZONES: [string, string, string[]][] = [
  ["rim", "Rim", ["rim"]],
  ["mid", "Mid", ["short mid", "long 2"]],
  ["three", "Three", ["corner three", "above the break three"]],
];
// Indented breakout rows under a zone, Rate = % of the parent zone: shot_profiles.py SUB_ZONES, so a player's rows
// match the team tables (both read core.shots.zone).
const SUB_ZONES: Record<string, [string, string, string[]][]> = {
  mid: [
    ["short_mid", "Short Mid", ["short mid"]],
    ["long2", "Long 2", ["long 2"]],
  ],
  three: [
    ["corner3", "Corner 3", ["corner three"]],
    ["atb3", "ATB 3", ["above the break three"]],
  ],
};

const THREE_BUCKETS: [string, string][] = [
  ["open_cs", "Open C&S"],
  ["contested_cs", "Contested C&S"],
  ["off_dribble", "Off-dribble"],
];

/** Absolute TS% colour: the report scale's seven buckets (util/reportColors tsHexFill), the drill-down's too. */
export const tsFill = (ts: number | null): string => tsHexFill(ts);

export type {
  ShotProfileCell,
  ShotProfileData,
  ShotProfileZoneRow,
  ShotProfileThreeRow,
} from "../../models/PlayerStats";

/** One lattice cell: its own shots (n, made, ts) and, through the drill-down's adjacent-bin merge
 * (ShotDetailModal/utils mergeAdjacentBins), its neighbourhood's, which colour, size, hide and hover read. */
export interface HexBin {
  x: number;
  y: number;
  n: number;
  made: number;
  ts: number;
  mergedN: number;
  mergedMade: number;
  mergedTs: number;
  /** The lattice key and the neighbours merged in: a hover outlines them all */
  key: string;
  neighborKeys: string[];
}

/** d3-hexbin's exact point → lattice cell assignment (personnel port). */
export function hexbin(shots: Shot[], r = HEX_R): HexBin[] {
  const dx = r * 2 * Math.sin(Math.PI / 3);
  const dy = r * 1.5;
  const bins = new Map<string, Shot[]>();
  for (const s of shots) {
    const py = s.y / dy;
    let pj = Math.round(py);
    const px = s.x / dx - (pj & 1) / 2;
    let pi = Math.round(px);
    const py1 = py - pj;
    if (Math.abs(py1) * 3 > 1) {
      const px1 = px - pi;
      const pi2 = pi + (px < pi ? -1 : 1) / 2;
      const pj2 = pj + (py < pj ? -1 : 1);
      const px2 = px - pi2;
      const py2 = py - pj2;
      if (px1 * px1 + py1 * py1 > px2 * px2 + py2 * py2) {
        pi = pi2 + (pj & 1 ? 1 : -1) / 2;
        pj = pj2;
      }
    }
    const key = `${pi},${pj}`;
    const arr = bins.get(key);
    if (arr) arr.push(s);
    else bins.set(key, [s]);
  }
  const own: { x: number; y: number; attempts: number; makes: number; ptsScored: number }[] = [];
  bins.forEach((pts, key) => {
    const [pi, pj] = key.split(",").map(Number);
    own.push({
      x: (pi + (Math.trunc(pj) & 1) / 2) * dx,
      y: pj * dy,
      attempts: pts.length,
      makes: pts.filter((s) => s.made).length,
      ptsScored: pts.reduce((a, s) => a + (s.ptsScored || 0), 0),
    });
  });
  const round = (v: number) => Math.round(v * 1000) / 1000;
  return mergeAdjacentBins(own, r).map((b) => ({
    x: b.x,
    y: b.y,
    n: b.attempts,
    made: b.makes,
    ts: round(b.ptsScored / (2 * b.attempts)),
    mergedN: b.mergedAttempts,
    mergedMade: b.mergedMakes,
    mergedTs: round(b.mergedTsPct),
    key: b.key,
    neighborKeys: b.neighborKeys,
  }));
}

/** One shot on the make / miss chart (chart frame: feet, baseline at top), with its hover details when the blob
 * carries them (types.ts GameShot): period, clock, and shooter / passer indexes into the chart's `names`. */
export interface CourtDot {
  x: number;
  y: number;
  made: boolean;
  q?: number;
  t?: number;
  s?: number;
  a?: number | null;
}

/** "Q4 3:45" / "OT 0:12" / "2OT 4:59": a shot's period and the game clock at release (seconds left). */
export function shotClockLabel(period: number, secondsLeft: number): string {
  const p = period <= 4 ? `Q${period}` : period === 5 ? "OT" : `${period - 4}OT`;
  const s = Math.max(0, Math.floor(secondsLeft));
  return `${p} ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// The hover card: the report's ink on white with a hairline, in the table face (web only: a tooltip never prints).
const SHOT_TIP_SX = REPORT_HOVER_CARD_SX;

/** The hover card's lines for a shot, or null when the blob predates the details (no tooltip then). */
export function shotTipLines(d: CourtDot, names?: string[]): [string, string, string] | null {
  if (!names || d.s == null || d.q == null || d.t == null || !names[d.s]) return null;
  const passer = d.a != null ? names[d.a] : undefined;
  // An attempt off a pass is "assisted" in the report's sense (AssistOpportunity), made or not
  const pass = passer ? `${d.made ? "Assist" : "Pass"}: ${passer}` : "Unassisted";
  return [names[d.s], shotClockLabel(d.q, d.t), pass];
}

// Make / miss marks for a single game, where hexbins are too sparse to read:
// dark filled circle = make, grey cross = miss (util/reportColors).
const { make: MAKE_FILL, miss: MISS_STROKE } = SHOT_MARKS;

/** Half court (baseline at top): hex bins sized by volume, colored by TS% —
 * or, with `dots`, one mark per shot (filled circle = make, cross = miss). */
export function CourtSvg({
  bins = [],
  dots,
  names,
  fill = tsFill,
  k = COURT_K,
  hexR = HEX_R,
  hideBelow = 0,
  refQuantile = 0.9,
  minSize = 0.32,
}: {
  bins?: HexBin[];
  /** Make / miss mode: plot these shots instead of hex bins */
  dots?: CourtDot[];
  /** Player names the dots' shooter / passer indexes resolve to: with them, a dot shows its details on hover */
  names?: string[];
  fill?: (ts: number | null) => string;
  /** px per foot — team charts render slightly smaller */
  k?: number;
  hexR?: number;
  /** Bins with fewer shots than this don't render (team charts: every
   * lattice cell has a stray shot or two — pure noise at team volume) */
  hideBelow?: number;
  /** Quantile of bin counts treated as "full size" — higher = fewer
   * saturated zones, more volume differentiation */
  refQuantile?: number;
  /** Smallest visible hex as a fraction of full size. Team charts need a
   * LOW floor: at team volume most bins sit below the reference, and a
   * high floor renders the whole midrange carpet at one uniform size. */
  minSize?: number;
}) {
  const [hovered, setHovered] = useState<HexBin | null>(null);
  const W = 50 * k;
  const H = COURT_CUT * k;
  const rimY = 5.25;
  const X = (x: number) => x * k;
  const Y = (y: number) => y * k;
  const line = { fill: "none", stroke: "#cfccc8", strokeWidth: 1.2 };
  const r3 = 23.75 * k;
  // Hidden, sized, coloured and hovered on the merged neighbourhood, as the drill-down chart
  const shown = bins.filter((b) => b.mergedN >= hideBelow);
  const nRef =
    shown.length > 0
      ? [...shown.map((b) => b.mergedN)].sort((a, b) => a - b)[
          Math.max(0, Math.ceil(shown.length * refQuantile) - 1)
        ] || 1
      : 1;
  const drawn = shown.filter((b) => b.y <= COURT_CUT - 1);
  const hexPoints = (b: HexBin) => {
    const mult = Math.max(minSize, Math.min(1.0, Math.sqrt(b.mergedN / nRef)));
    const rr = hexR * k * mult;
    const cx = X(b.x);
    const cy = Y(b.y);
    return Array.from({ length: 6 }, (_, j) => {
      const a = (Math.PI / 180) * (60 * j - 30);
      return `${(cx + rr * Math.cos(a)).toFixed(1)},${(cy + rr * Math.sin(a)).toFixed(1)}`;
    }).join(" ");
  };
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <rect x={0} y={0} width={W} height={H} fill="#fff" stroke="#4c4c4c" strokeWidth={1.5} />
      <rect x={X(17)} y={0} width={X(16)} height={Y(19)} {...line} />
      <circle cx={X(25)} cy={Y(19)} r={6 * k} {...line} />
      <line x1={X(22)} y1={Y(4)} x2={X(28)} y2={Y(4)} stroke="#4c4c4c" strokeWidth={1.5} />
      <circle cx={X(25)} cy={Y(rimY)} r={0.75 * k} fill="none" stroke="#4c4c4c" strokeWidth={1.2} />
      <path
        d={`M ${X(21)} ${Y(rimY)} A ${4 * k} ${4 * k} 0 0 0 ${X(29)} ${Y(rimY)}`}
        {...line}
      />
      <line x1={X(3)} y1={0} x2={X(3)} y2={Y(14)} {...line} />
      <line x1={X(47)} y1={0} x2={X(47)} y2={Y(14)} {...line} />
      <path d={`M ${X(3)} ${Y(14)} A ${r3} ${r3} 0 0 0 ${X(47)} ${Y(14)}`} {...line} />
      {drawn.map((b, i) => {
          const pts = hexPoints(b);
          const color = fill(b.mergedTs);
          // The drill-down chart's hover line (made / shots, TS%); a tooltip is a portal, so print is unchanged
          return (
            <Tooltip
              key={i}
              disableInteractive
              placement="top"
              enterDelay={0}
              componentsProps={{ tooltip: { sx: SHOT_TIP_SX } }}
              title={hexTipText(b.mergedMade, b.mergedN, b.mergedTs)}
            >
              <polygon
                data-testid="hex"
                points={pts}
                fill={color}
                fillOpacity={0.85}
                stroke="#e6e4e1"
                strokeWidth={0.4}
                style={{ cursor: "default" }}
                onMouseEnter={() => setHovered(b)}
                onMouseLeave={() => setHovered(null)}
              />
            </Tooltip>
          );
        })}
      {/* The hovered hex and the neighbours its numbers include, outlined in black like the drill-down chart's
          (HOVER_STROKE_COLOR / _WIDTH): drawn last so no neighbour covers an edge, and never a hover target */}
      {hovered &&
        drawn
          .filter((b) => b.key === hovered.key || hovered.neighborKeys.includes(b.key))
          .map((b) => (
            <polygon
              key={`o${b.key}`}
              data-testid="hex-outline"
              points={hexPoints(b)}
              fill="none"
              stroke="#000"
              strokeWidth={2}
              pointerEvents="none"
            />
          ))}
      {(dots ?? [])
        .filter((d) => d.y <= COURT_CUT - 0.5)
        .map((d, i) => {
          const cx = X(d.x);
          const cy = Y(d.y);
          const r = 0.55 * k;
          return d.made ? (
            <circle
              key={`d${i}`}
              className="make"
              cx={cx}
              cy={cy}
              r={r}
              fill={MAKE_FILL}
              stroke="#fff"
              strokeWidth={0.6}
            />
          ) : (
            <path
              key={`d${i}`}
              className="miss"
              d={`M ${cx - r} ${cy - r} L ${cx + r} ${cy + r} M ${cx - r} ${cy + r} L ${cx + r} ${cy - r}`}
              stroke={MISS_STROKE}
              strokeWidth={1.3}
              strokeLinecap="round"
            />
          );
        })}
      {/* Hover targets over the marks, drawn last so a mark never hides one: transparent, a little larger than
          the mark (a miss's cross is thin), and inside the svg, so they take no layout and print as nothing */}
      {(dots ?? [])
        .filter((d) => d.y <= COURT_CUT - 0.5)
        .map((d, i) => {
          const lines = shotTipLines(d, names);
          if (!lines) return null;
          return (
            <Tooltip
              key={`h${i}`}
              disableInteractive
              placement="top"
              enterDelay={0}
              componentsProps={{ tooltip: { sx: SHOT_TIP_SX } }}
              title={
                <>
                  <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
                    {lines[0]}
                  </Box>
                  <Box component="span" sx={{ display: "block" }}>
                    {lines[1]}
                  </Box>
                  <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
                    {lines[2]}
                  </Box>
                </>
              }
            >
              <circle
                className="shot-hit"
                data-testid="shot-hit"
                cx={X(d.x)}
                cy={Y(d.y)}
                r={0.95 * k}
                fill="transparent"
                style={{ cursor: "default" }}
              />
            </Tooltip>
          );
        })}
    </svg>
  );
}

interface ZoneRow {
  key: string;
  label: string;
  tsa: number;
  share: number;
  efg: number | null;
  ftr: number | null;
  xts: number | null;
  ts: number | null;
  shotmaking: number | null;
  children?: ZoneRow[];
}

/** One zone's values (shot_profiles.py `_zone_values`): eFG% over field goal attempts, everything else over trips. */
function zoneValues(sub: Shot[]): Pick<ZoneRow, "efg" | "ftr" | "xts" | "ts" | "shotmaking"> {
  const tsa = sub.length;
  if (!tsa) return { efg: null, ftr: null, xts: null, ts: null, shotmaking: null };
  const fga = sub.filter(isFga).length;
  const efg = fga ? sub.reduce((a, s) => a + (s.made ? (s.three ? 1.5 : 1) : 0), 0) / fga : null;
  // Foul %: fouled trips / trips, and-ones included
  const ftr = sub.filter((s) => s.fouled).length / tsa;
  const ts = sub.reduce((a, s) => a + (s.ptsScored || 0), 0) / (2 * tsa);
  // xTS prices the whole trip: qsq is expected eFG so it misses FT value,
  // and a qsq-only xTS makes shotmaking ≈ the foul rate. The blob's math
  // (shot_profiles.py) gives fouled shots the league-empirical E[pts] for
  // their zone; this chip-less fallback has no league table, so fouled
  // trips take their own actual points — neutral to shotmaking.
  const q = sub.filter((s) => !s.fouled && s.qsq !== null && s.qsq !== undefined);
  const fouledSub = sub.filter((s) => s.fouled);
  const xDenom = q.length + fouledSub.length;
  const xts = xDenom
    ? (q.reduce((a, s) => a + (s.qsq as number), 0) +
        fouledSub.reduce((a, s) => a + (s.ptsScored || 0) * 50, 0)) /
      xDenom
    : null;
  return { efg, ftr, xts, ts, shotmaking: xts !== null ? ts * 100 - xts : null };
}

export function computeZones(shots: Shot[]): ZoneRow[] {
  // Trips on the engine's rule: a missed heave is not one (shot_profiles.py `_entity_rows`)
  const trips = shots.filter(isTsa);
  const total = trips.length;
  if (!total) return [];
  const inRegions = (list: Shot[], regions: string[]) =>
    list.filter((s) => s.region !== null && regions.indexOf(s.region) >= 0);
  return COARSE_ZONES.map(([key, label, regions]) => {
    const sub = inRegions(trips, regions);
    const children = (SUB_ZONES[key] ?? []).map(([ckey, clabel, cregions]) => {
      const csub = inRegions(sub, cregions);
      return {
        key: ckey,
        label: clabel,
        tsa: csub.length,
        share: sub.length ? csub.length / sub.length : 0,
        ...zoneValues(csub),
      };
    });
    return {
      key,
      label,
      tsa: sub.length,
      share: sub.length / total,
      ...zoneValues(sub),
      ...(children.length ? { children } : {}),
    };
  });
}

interface ThreeRow {
  key: string;
  label: string;
  fg3a: number;
  share: number;
  x3p: number | null;
  p3: number | null;
  lift: number | null;
}

function threeBucket(s: Shot): string {
  if (s.catchAndShoot && (s.contestLevel === "uncontested" || s.contestLevel === "lightly_contested"))
    return "open_cs";
  if (s.catchAndShoot) return "contested_cs";
  return "off_dribble";
}

export function computeThrees(shots: Shot[]): ThreeRow[] | null {
  const threes = shots.filter((s) => s.three && isTsa(s));
  // C&S flags absent (off-provider) -> the bucket split is meaningless.
  if (!threes.length || threes.every((s) => s.catchAndShoot === null)) return null;
  const total = threes.length;
  return THREE_BUCKETS.map(([key, label]) => {
    const sub = threes.filter((s) => threeBucket(s) === key);
    const fg3a = sub.length;
    // 3P% and x3P% on attempts (shot_profiles.py `_three_values`); the fg3a count and floor stay on trips
    const att = sub.filter(isFga);
    if (!att.length) return { key, label, fg3a, share: fg3a / total, x3p: null, p3: null, lift: null };
    const p3 = (100 * att.filter((s) => s.made).length) / att.length;
    const q = att.filter((s) => s.qsq !== null && s.qsq !== undefined);
    // qsq is expected eFG (0-100); /1.5 gives expected 3P% (personnel pull #25L).
    const x3p = q.length ? q.reduce((a, s) => a + (s.qsq as number), 0) / q.length / 1.5 : null;
    return {
      key,
      label,
      fg3a,
      share: fg3a / total,
      x3p,
      p3,
      lift: x3p !== null ? p3 - x3p : null,
    };
  });
}

// Hover reveal for the condensed chips (and the numeraled ones): the
// stored rank / percentile in words.
const scoreTitle = (cell: ShotProfileCell): string | undefined =>
  cell.rank !== undefined
    ? `${nth(cell.rank)} of ${cell.rankPopulation ?? 30}`
    : cell.percentile !== undefined
      ? `${nth(cell.percentile)} percentile`
      : undefined;

// Chip cells: quality metrics render the condensed color chip; only the
// headline TS% / 3P% carry the numeral (team rank ordinal / player
// percentile digits).
const chipCell = (
  cell: ShotProfileCell | null | undefined,
  format: (v: number) => string,
  numeral: boolean,
  cellKey: string,
) => {
  if (!cell) {
    return (
      <TableCell sx={CELL_SX} key={cellKey}>
        —
      </TableCell>
    );
  }
  const scored = cell.rank !== undefined || cell.percentile !== undefined;
  return (
    <TableCell sx={CELL_SX} key={cellKey}>
      {scored ? (
        <Tooltip title={scoreTitle(cell) ?? ""} disableInteractive>
          {/* span: PercentileChip doesn't forward the tooltip's ref */}
          <span>
            <PercentileChip
              value={format(cell.value)}
              numeral={
                numeral
                  ? cell.rank !== undefined
                    ? nth(cell.rank)
                    : String(cell.percentile)
                  : undefined
              }
              percentile={getPercentileFromRankOrPercentile(
                cell.percentile,
                cell.rank,
                cell.rankPopulation,
              )}
              rank={colorRank(cell.percentile, cell.rank)}
              rankPopulation={cell.rankPopulation}
              fontSize="0.828rem"
              minWidth={28}
            />
          </span>
        </Tooltip>
      ) : (
        format(cell.value)
      )}
    </TableCell>
  );
};

// Share cells: plain value + the freq needle (rotation = league
// percentile of the share) — tendencies get a direction cue, never the
// quality ramp.
// Share cells: the value + its rank / percentile as a small grey number (a tendency, never the quality ramp); the
// number replaced the freq needle (Trevor, 2026-10-10). The rank / percentile in words on hover.
const numeralCell = (cell: ShotProfileCell | null | undefined, cellKey: string) => (
  <TableCell sx={CELL_SX} key={cellKey}>
    {cell ? (
      <Tooltip title={scoreTitle(cell) ?? ""} disableInteractive>
        <span>
          {`${cell.value.toFixed(0)}%`}
          <GreyNumeral>
            {cell.rank !== undefined
              ? nth(cell.rank)
              : cell.percentile !== undefined
                ? String(cell.percentile)
                : undefined}
          </GreyNumeral>
        </span>
      </Tooltip>
    ) : (
      "—"
    )}
  </TableCell>
);

// Team charts read a zone's share as good or bad (Trevor, 2026-10-10): more rim and three and less mid is
// better on offense, the reverse on defense. The blob scores shares on magnitude (shot_profiles: rank 1 /
// percentile 100 = the largest share), so the cell flips it where smaller is better.
const SHARE_HIGHER_BETTER: Record<string, boolean> = { rim: true, mid: false, three: true };

export function shareQualityCell(
  zoneKey: string,
  cell: ShotProfileCell | null | undefined,
  perspective: "offense" | "defense",
): ShotProfileCell | null | undefined {
  const higher = SHARE_HIGHER_BETTER[zoneKey];
  if (!cell || higher === undefined || higher === (perspective === "offense")) return cell;
  return {
    ...cell,
    percentile: cell.percentile !== undefined ? 100 - cell.percentile : undefined,
    rank:
      cell.rank !== undefined ? (cell.rankPopulation ?? 30) + 1 - cell.rank : undefined,
  };
}


const SUBH_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.828rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  mt: 1,
  mb: 0.25,
};
const FOOTNOTE_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.713rem",
  color: "text.secondary",
  mt: 0.25,
};
const LOW_ROW_SX = { opacity: 0.45 };

const fmtSigned = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}`;
const fmt1 = (v: number) => v.toFixed(1);

export type ShotZoneColumn =
  | "share"
  | "tsa"
  | "efg"
  | "ftr"
  | "xts"
  | "ts"
  | "shotmaking";

const ZONE_COLUMN_LABELS: Record<ShotZoneColumn, string> = {
  share: "Rate",
  tsa: "TSA",
  efg: "eFG%",
  ftr: "Foul %",
  xts: "Shot Qual",
  ts: "TS%",
  shotmaking: "Shotmaking",
};
// Column help on hover (the blob key for Foul % stays `ftr`)
const ZONE_COLUMN_TITLES: Partial<Record<ShotZoneColumn, string>> = {
  tsa: "Shot attempts: field goal attempts plus fouled misses (a missed heave is not counted)",
  efg: "eFG%: (FGM + 0.5 × 3PM) / FGA. A fouled miss is not a field goal attempt",
  ftr: "Foul %: share of shot attempts fouled, and-ones included",
};
const ALL_ZONE_COLUMNS: ShotZoneColumn[] = [
  "share",
  "tsa",
  "efg",
  "ftr",
  "xts",
  "ts",
  "shotmaking",
];

/**
 * The shot-distribution table: zones (Rate = share of shots, with the freq
 * needle) and their indented sub-zone breakouts (Rate = % of the parent zone).
 * Rows under `lowFloor` attempts dim; their scoring comes from the blob, which
 * leaves under-floor cells unscored.
 */
export function ShotZoneTable({
  zones,
  hasX,
  columns = ALL_ZONE_COLUMNS,
  labels,
  lowFloor = 25,
  ratePerspective,
}: {
  zones: ShotProfileData["zones"];
  /** The profile carries shot-quality values (Shot Qual, Shotmaking) */
  hasX: boolean;
  /** Column subset, in display order */
  columns?: ShotZoneColumn[];
  /** Header relabels (e.g. Shot Qual → the stacked "Shot Quality" head) */
  labels?: Partial<Record<ShotZoneColumn, React.ReactNode>>;
  /** Attempts below which a row dims */
  lowFloor?: number;
  /** Team charts: the zone rows' Rate is a good / bad chip on this side's direction. Player charts leave it off (a
   * player's mix is a role, not good or bad); every other Rate is a grey number. */
  ratePerspective?: "offense" | "defense";
}) {
  const shown = columns.filter(
    (c) => hasX || (c !== "xts" && c !== "shotmaking"),
  );
  const cells = (z: ShotProfileData["zones"][number], isChild: boolean) =>
    shown.map((c) => {
      switch (c) {
        case "share":
          return ratePerspective && !isChild
            ? chipCell(
                shareQualityCell(z.key, z.share, ratePerspective),
                (v) => `${v.toFixed(0)}%`,
                true,
                "share",
              )
            : numeralCell(z.share, "share");
        case "tsa":
          return (
            <TableCell sx={CELL_SX} key="tsa">
              {z.tsa}
            </TableCell>
          );
        case "ts":
          return chipCell(z.ts, fmt1, true, "ts");
        case "shotmaking":
          return chipCell(z.shotmaking, fmtSigned, false, "sm");
        default:
          return chipCell(z[c], fmt1, false, c);
      }
    });
  return (
    <Table size="small" sx={ZEBRA_SX}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...HEAD_SX, textAlign: "left" }}>Zone</TableCell>
          {shown.map((c) => (
            <TableCell sx={HEAD_SX} key={c} title={ZONE_COLUMN_TITLES[c]}>
              {labels?.[c] ?? ZONE_COLUMN_LABELS[c]}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {zones.flatMap((z) => [
          <TableRow key={z.key} sx={z.tsa < lowFloor ? LOW_ROW_SX : undefined}>
            <TableCell sx={ROW_LABEL_SX}>{z.label}</TableCell>
            {cells(z, false)}
          </TableRow>,
          // Sub-zone breakouts: indented + dimmed; their Rate is
          // % of the parent zone (user call 2026-09-10).
          ...(z.children ?? []).map((c) => (
            <TableRow
              key={c.key}
              sx={{
                opacity: c.tsa < lowFloor ? 0.45 : 0.72,
              }}
            >
              <TableCell sx={{ ...ROW_LABEL_SX, fontWeight: 400, pl: 2.5 }}>
                {c.label}
              </TableCell>
              {cells(c, true)}
            </TableRow>
          )),
        ])}
      </TableBody>
    </Table>
  );
}


/** Team hexbin settings (the coaches' season chart and the ownership season block): bins under ~0.12% of all shots
 * are noise at team volume, and the low floor lets thin midrange cells render small instead of flattening at one
 * uniform floored size. The 5-shot minimum belongs to a full season's volume: it ramps in over the first 2,000 shots
 * (a two-game season, 187 shots, had hidden every bin but the rim's; Trevor's season packet, 2026-10-27). */
export const teamHexProps = (nShots: number) => ({
  hideBelow: Math.max(1, Math.min(5, Math.round(nShots / 400)), Math.round(nShots * 0.0012)),
  refQuantile: 0.9,
  minSize: 0.15,
});

/** A chart's shots: one shooter's season, or a team's for / against (the shot_detail endpoint adds Season,
 * SeasonType and Garbage itself when a season is given). `contextFilters` scope a team's shots like a report row:
 * the HC pages pass their Overall row's (halfcourt), so the chart shows the shots its drill-down does. `skip` (a game
 * profile carries its own shots) fetches nothing. */
export function useShotChartShots({
  playerUuid,
  teamNbaId,
  perspective,
  season,
  seasonType,
  reportSource,
  contextFilters,
  skip = false,
}: {
  playerUuid?: string;
  teamNbaId?: string;
  perspective?: "offense" | "defense";
  season: number;
  seasonType?: string;
  reportSource?: ReportSourceInfo;
  contextFilters?: ShotQueryContext["filters"];
  skip?: boolean;
}) {
  const filterKey = JSON.stringify(contextFilters ?? []);
  const api = useLeagueApi();
  const [shots, setShots] = useState<Shot[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShots(null);
    setFailed(false);
    if (skip) return;
    const fetchShots = playerUuid
      ? api.getShotDetailByFilters([
          { name: "Shooter", op: "in", values: [playerUuid] },
          { name: "Season", op: "in", values: [String(season)] },
          { name: "SeasonType", op: "in", values: ["regular season"] },
          { name: "Garbage", op: "in", values: [false] },
        ])
      : // Season/SeasonType/Garbage are added by the endpoint itself when a
        // season is given — only the perspective scoping goes in the context.
        api.getShotDetail(
          teamNbaId!,
          String(season),
          {
            table: "OVERVIEW",
            section: "Shot Chart",
            perspective: perspective ?? "offense",
            filters: JSON.parse(filterKey),
            scope: reportSource?.scope ?? null,
            source: reportSource?.source ?? null,
          },
          seasonType,
        );
    fetchShots
      .then((res) => {
        if (!cancelled) setShots(res.shots ?? []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    api,
    skip,
    playerUuid,
    teamNbaId,
    perspective,
    season,
    seasonType,
    reportSource?.scope,
    reportSource?.source,
    filterKey,
  ]);

  return { shots, failed };
}

export default function ShotChartSection({
  code,
  playerUuid,
  teamNbaId,
  perspective,
  season,
  seasonType,
  seasonLabel,
  title = "Shot Chart",
  layout = "beside",
  profile,
  reportSource,
  gameProfile,
  contextFilters,
}: {
  code?: string;
  /** Player mode — fetch this shooter's shots */
  playerUuid?: string;
  /** Team mode — fetch the team's shots for/against (with `perspective`) */
  teamNbaId?: string;
  perspective?: "offense" | "defense";
  season: number;
  seasonType?: string;
  seasonLabel?: string;
  title?: string;
  /** "beside" = chart left, tables right (player pages);
   *  "stacked" = chart above, tables below (team overview grid) */
  layout?: "beside" | "stacked";
  /** Blob-stored table data (values + rank/percentile). Without it the
   * tables fall back to client-computed values, chip-less. */
  profile?: ShotProfileData | null;
  /** Team mode: the report's provider, so a dev `?source=` report charts it */
  reportSource?: ReportSourceInfo;
  /** Game report: the game's stored halfcourt profile and shots (make / miss dots, no fetch) */
  gameProfile?: GameShotProfileSide | null;
  /** Team mode: the report row whose shots the chart shows (the HC pages' Overall row: halfcourt) */
  contextFilters?: ShotQueryContext["filters"];
}) {
  const { shots, failed } = useShotChartShots({
    playerUuid,
    teamNbaId,
    perspective,
    season,
    seasonType,
    reportSource,
    contextFilters,
    skip: !!gameProfile,
  });

  const isTeam = !playerUuid;
  const courtK = isTeam ? 7 : COURT_K;
  // Team hexes are the same size as player charts (1.75x and 1.25x were both
  // tried and walked back); the team-mode differentiation comes from the
  // thresholds below — volume-scaled hide floor + low min size.
  const bins = useMemo(
    () => (shots ? hexbin(shots.filter((s) => s.x != null)) : []),
    [shots],
  );

  // Table data: blob profile when provided (values + rank/percentile),
  // otherwise client-computed values with no scoring.
  const data: ShotProfileData | null = useMemo(() => {
    if (gameProfile) return gameProfile;
    if (profile) return profile;
    if (!shots) return null;
    const cell = (v: number | null): ShotProfileCell | null =>
      v === null ? null : { value: Math.round(v * 10) / 10 };
    const toRow = (z: ZoneRow): ShotProfileData["zones"][number] => ({
      key: z.key,
      label: z.label,
      tsa: z.tsa,
      share: { value: Math.round(z.share * 1000) / 10 },
      efg: cell(z.efg === null ? null : z.efg * 100),
      ftr: cell(z.ftr === null ? null : z.ftr * 100),
      xts: cell(z.xts),
      ts: cell(z.ts === null ? null : z.ts * 100),
      shotmaking: cell(z.shotmaking),
      ...(z.children ? { children: z.children.map(toRow) } : {}),
    });
    const zones = computeZones(shots).map(toRow);
    const t3 = computeThrees(shots);
    return {
      zones,
      threes: t3
        ? t3.map((t) => ({
            key: t.key,
            label: t.label,
            fg3a: t.fg3a,
            share: { value: Math.round(t.share * 1000) / 10 },
            x3p: cell(t.x3p),
            p3: cell(t.p3),
            lift: cell(t.lift),
          }))
        : null,
    };
  }, [gameProfile, profile, shots]);

  if (failed) return null;
  const hasX = (data?.zones ?? []).some((z) => z.xts != null);
  // Shotmaking reads the shooters, so it belongs to the offense only (TR3)
  const showShotmaking = perspective !== "defense";
  const zoneColumns = ALL_ZONE_COLUMNS.filter(
    (c) => showShotmaking || c !== "shotmaking",
  );
  const note = gameProfile
    ? `${gameProfile.shots.length.toLocaleString()} halfcourt shots`
    : shots
      ? `${seasonLabel ?? season} · ${shots.length.toLocaleString()} shots`
      : "loading…";

  return (
    // Not kept together in print (no break-inside: avoid): a pushed block prints over the next section
    // (stylesheet.scss, 2026-10-11). The block sits at the top of its page, so it rarely meets a page end.
    <Box>
      <SectionHead code={code} title={title} note={note} />
      {!gameProfile && shots && shots.length === 0 ? (
        <Typography sx={FOOTNOTE_SX}>No shots recorded for this scope.</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              layout === "stacked" ? "1fr" : { xs: "1fr", md: "auto 1fr" },
            columnGap: 4,
            rowGap: layout === "stacked" ? 1 : 0,
            alignItems: "start",
            // Paper width < md breakpoint — keep the chart beside the tables in print.
            "@media print": {
              gridTemplateColumns: layout === "stacked" ? "1fr" : "auto 1fr",
            },
          }}
        >
          <Box sx={{ minHeight: COURT_CUT * courtK }}>
            {gameProfile && (
              <CourtSvg dots={gameProfile.shots} names={gameProfile.names} k={courtK} />
            )}
            {!gameProfile && shots && (
              <CourtSvg
                bins={bins}
                k={courtK}
                // Team thresholds scale with volume: bins under ~0.12% of
                // all shots are noise; the low floor lets thin midrange
                // cells actually render small instead of flattening at one
                // uniform floored size.
                {...(isTeam ? teamHexProps(shots.length) : { hideBelow: 0, refQuantile: 0.9, minSize: 0.32 })}
              />
            )}
            <Typography sx={FOOTNOTE_SX}>
              {gameProfile ? (
                <ShotMarksKey shots={gameProfile.shots} />
              ) : (
                "Hex size = volume; color = absolute TS% at that spot."
              )}
            </Typography>
          </Box>
          <Stack spacing={0}>
            <Typography sx={SUBH_SX}>Shot distribution</Typography>
            <ShotZoneTable
              zones={data?.zones ?? []}
              hasX={hasX}
              columns={zoneColumns}
              lowFloor={gameProfile ? GAME_SHOT_SAMPLE_FLOOR : undefined}
              ratePerspective={isTeam ? perspective ?? "offense" : undefined}
              labels={isTeam ? { xts: shotQualLabel("TS%") } : undefined}
            />
            {hasX && (
              <Typography sx={FOOTNOTE_SX}>
                Shot Qual = expected TS% from shot quality
                {showShotmaking ? "; Shotmaking = TS% − Shot Qual." : "."}
                {(data?.zones ?? []).some((z) => z.children?.length) &&
                  " Sub-zone rates are % of the parent zone."}
              </Typography>
            )}

            {data?.threes && (
              <>
                <Typography sx={SUBH_SX}>3PT Shot Detail</Typography>
                <Table size="small" sx={ZEBRA_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...HEAD_SX, textAlign: "left" }} />
                      <TableCell sx={HEAD_SX}>Share</TableCell>
                      <TableCell sx={HEAD_SX}>3PA</TableCell>
                      {hasX && (
                        <TableCell sx={HEAD_SX}>
                          {isTeam ? shotQualLabel("3P%") : "Shot Qual"}
                        </TableCell>
                      )}
                      <TableCell sx={HEAD_SX}>3P%</TableCell>
                      {hasX && showShotmaking && (
                        <TableCell sx={HEAD_SX}>Shotmaking</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.threes.map((t) => (
                      <TableRow
                        key={t.key}
                        sx={t.fg3a < (gameProfile ? GAME_SHOT_SAMPLE_FLOOR : 40) ? LOW_ROW_SX : undefined}
                      >
                        <TableCell sx={ROW_LABEL_SX}>{t.label}</TableCell>
                        {numeralCell(t.share, "share")}
                        <TableCell sx={CELL_SX}>{t.fg3a}</TableCell>
                        {hasX && chipCell(t.x3p, fmt1, false, "x3p")}
                        {chipCell(t.p3, fmt1, true, "p3")}
                        {hasX &&
                          showShotmaking &&
                          chipCell(t.lift, fmtSigned, false, "lift")}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Typography sx={FOOTNOTE_SX}>
                  Open = uncontested or lightly contested.
                  {hasX && " Shot Qual = expected 3P% from shot quality"}
                  {hasX &&
                    (showShotmaking ? "; Shotmaking = 3P% − Shot Qual." : ".")}
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
